import React, { useEffect } from 'react';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@rocco/components/popover';
import { RadioGroup, RadioGroupItem } from '@rocco/components/radio-group';
import { Button } from '@rocco/components/button';
import { ReactComponent as ArrowChevronDownIcon } from '@rocco/icons/svg/arrow/arrow-chevron-down.svg';
import {
    type FilterSortOption,
    useFiltersContext,
} from '@rocco/ui/filter-sort/components/Filters';
import {
    validateSortOption,
    type SortOption,
} from '@rocco/types/enum/sort-option';
import { useBoolean } from 'ahooks';

interface SortDropdownProps {
    sortOptions: FilterSortOption[];
    updateSort?: (sortOption: SortOption) => void;
    /**
     * Optional location object from Remix Router (pathname, search).
     * When provided, the dropdown will auto-close on navigation.
     * Used in Remix contexts; omit for non-Remix contexts.
     */
    location?: { pathname: string; search: string } | null;
    /** Trigger label shown when no option is selected. Defaults to "Sort By". */
    label?: string;
}

// Internal component that uses the Filters context
const SortDropdownWithContext = ({
    sortOptions,
    location,
    label,
}: {
    sortOptions: FilterSortOption[];
    location?: { pathname: string; search: string } | null;
    label?: string;
}) => {
    const { searchFilters } = useFiltersContext();
    const { updateSort } = searchFilters;

    return (
        <SortDropdownContent
            sortOptions={sortOptions}
            updateSort={updateSort}
            location={location}
            label={label}
        />
    );
};

// Internal component that receives updateSort as prop
const SortDropdownContent = ({
    sortOptions,
    updateSort,
    location,
    label = 'Sort By',
}: {
    sortOptions: FilterSortOption[];
    updateSort: (sortOption: SortOption) => void;
    location?: { pathname: string; search: string } | null;
    label?: string;
}) => {
    const activeOption = sortOptions.find((option) => option.selected);
    const defaultOption = activeOption?.value ?? sortOptions?.[0]?.value ?? '';

    // The popover closes on every selection, valid or not: a value the option
    // list offered but `SortOption` does not recognise is a wiring fault, and
    // leaving the popover open makes it read as an unresponsive control.
    const handleSortChange = (value: string) => {
        const sortOption = validateSortOption(value);

        setIsOpen(false);

        if (!sortOption) {
            console.warn(`SortDropdown: unrecognised sort option "${value}"`);
            return;
        }

        updateSort(sortOption);
    };

    const [isOpen, { set: setIsOpen, setFalse: closeSortDropdown }] =
        useBoolean(false);

    useEffect(() => {
        let lastScrollY = window.scrollY;

        const handleScroll = () => {
            const scrollDiff = Math.abs(window.scrollY - lastScrollY);
            if (scrollDiff > 300) {
                closeSortDropdown();
                lastScrollY = window.scrollY;
            }
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [closeSortDropdown]);

    // Close popover when location changes (e.g., after sorting/navigation)
    // Only runs when location prop is provided (Remix contexts)
    useEffect(() => {
        if (location) {
            setIsOpen(false);
        }
    }, [location, setIsOpen]);

    return sortOptions.length && sortOptions[0] ? (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
                <Button color="transparent" size="compact">
                    <span>{activeOption?.title ?? label}</span>
                    <ArrowChevronDownIcon className="ml-2 size-4.5 transition-transform group-data-[state=open]:transform-[rotateX(180deg)]" />
                </Button>
            </PopoverTrigger>
            <PopoverContent align="end" paddings="small">
                <RadioGroup
                    defaultValue={defaultOption}
                    onValueChange={handleSortChange}
                >
                    {sortOptions.map((option, index) => (
                        <label
                            key={index}
                            className="flex cursor-pointer items-center gap-x-3 rounded-2 px-2 py-2.5 hover:bg-gray-50"
                        >
                            <RadioGroupItem value={option.value} />
                            <span className="text-base">{option.title}</span>
                        </label>
                    ))}
                </RadioGroup>
            </PopoverContent>
        </Popover>
    ) : null;
};

export const SortDropdown = (props: SortDropdownProps) => {
    const { sortOptions, updateSort: updateSortProp, location, label } = props;

    // If updateSort prop is provided, use the content component directly
    if (updateSortProp) {
        return (
            <SortDropdownContent
                sortOptions={sortOptions}
                updateSort={updateSortProp}
                location={location}
                label={label}
            />
        );
    }

    // Otherwise, use the context-based component
    return (
        <SortDropdownWithContext
            sortOptions={sortOptions}
            location={location}
            label={label}
        />
    );
};
